
import { supabase } from '@/lib/supabase/client';
import { executeWithTimeout } from '@/lib/supabase';
import { debugLog, debugError } from '@/utils/debugLogger';

/**
 * Create user role via RPC
 * @param userId User ID
 * @returns Success status
 */
export const createUserRoleViaRPC = async (userId: string) => {
  debugLog('auth', 'Creating role via RPC for user:', userId, 'RoleCreationService');
  
  try {
    // Attempt to call the RPC function with lowercase 'user'
    const { data, error } = await supabase.rpc('create_user_role', { 
      user_id_input: userId,
      role_input: 'user'  // lowercase to match database role name
    });
    
    if (error) {
      if (error.message.includes('function') && error.message.includes('not found')) {
        debugLog('auth', 'RPC function not found, need to use fallback', undefined, 'RoleCreationService');
        return false;
      }
      
      debugError('auth', 'RPC error:', error, 'RoleCreationService');
      return false;
    }
    
    debugLog('auth', 'Role created successfully via RPC', undefined, 'RoleCreationService');
    return true;
  } catch (error) {
    debugError('auth', 'Exception in createUserRoleViaRPC:', error, 'RoleCreationService');
    return false;
  }
};

/**
 * Insert user role directly using role_id (integer)
 * @param userId User ID
 * @returns Success status
 */
export const insertUserRoleDirectly = async (userId: string) => {
  debugLog('auth', 'Inserting role directly for user:', userId, 'RoleCreationService');
  
  try {
    // Use upsert with role_id=4 ('user' role) and onConflict on user_id
    const { error } = await executeWithTimeout(
      async () => await supabase
        .from('user_roles')
        .upsert(
      [{ user_id: userId, role_id: 4 }],  // role_id 4 = 'user'
          { onConflict: 'user_id', ignoreDuplicates: true }
        ),
      5000
    );
    
    if (!error) {
      debugLog('auth', 'Role created/updated with role_id=4', undefined, 'RoleCreationService');
      return true;
    }
    
    debugError('auth', 'Upsert failed:', error, 'RoleCreationService');
    
    // Fallback: try simple insert (in case upsert syntax differs)
    const { error: insertError } = await executeWithTimeout(
      async () => await supabase
        .from('user_roles')
        .insert([{ user_id: userId, role_id: 4 }]),
      5000
    );
    
    if (!insertError) {
      debugLog('auth', 'Role created with insert fallback', undefined, 'RoleCreationService');
      return true;
    }
    
    // If insert also fails (likely due to duplicate), that's OK - role exists
    if (insertError.code === '23505') {  // unique_violation
      debugLog('auth', 'Role already exists for user', undefined, 'RoleCreationService');
      return true;
    }
    
    debugError('auth', 'Insert fallback failed:', insertError, 'RoleCreationService');
    return false;
  } catch (error) {
    debugError('auth', 'Exception in insertUserRoleDirectly:', error, 'RoleCreationService');
    return false;
  }
};
